// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Collapse, IconButton, Switch, Typography } from "@mui/material"
import { ChangeEvent, FC, useContext, useEffect, useState } from "react"
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from "../common/CustomModal"
// others
import { UpdateLawTypeInput, useUpdateLawTypeMutation } from '../../../generated'
import { GENERAL_INFORMATION } from "../../constants"
import { AppContext, AuthContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { DispatchAndState, ErrorException } from "../../interfaceTypes"
import { titleize } from "../../utils"
import { createLawTypeValidationSchema } from '../../validationSchema'
import { LawTypeAction, LawTypeActionType, LawTypeState } from "./LawTypeReducer"

export const ViewCurrentLawType: FC<DispatchAndState<LawTypeState, LawTypeAction>> = ({ state, dispatch, refetch, loading }) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { isEdit, dispatch: appDispatch } = useContext(AppContext)
  const { isAdmin } = useContext(AuthContext)
  const { currentLawType } = state || {}
  const { id, name, syncWithOutlook } = currentLawType || {}

  const handleClose = () => {
    appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
    setIsEditOpen(false)
    reset()
  }

  const methods = useForm<UpdateLawTypeInput>({
    mode: 'all',
    resolver: yupResolver(createLawTypeValidationSchema),

    defaultValues: {
      name: titleize(name || ''),
      syncWithOutlook: syncWithOutlook || false
    }
  })

  const { handleSubmit, reset, setValue, control } = methods;

  const [updateLawType, { loading: isLoading }] = useUpdateLawTypeMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        const { updateLawType: { response, lawType } } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && lawType) {
            refetch && refetch()
            Alert.success('Law type updated successfully')
            dispatch({ type: LawTypeActionType.SET_CURRENT_LAW_TYPE, currentLawType: lawType })
            reset()
            setIsEditOpen(false);
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<UpdateLawTypeInput> = async (data: UpdateLawTypeInput) => {
    const { name, syncWithOutlook } = data

    if (id) {
      await updateLawType({
        variables: {
          updateLawTypeInput: {
            name,
            id,
            syncWithOutlook
          }
        }
      })
    }
  }

  const handleChangeSwitch = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setValue("syncWithOutlook", newValue)
  };

  useEffect(() => {
    if (!isEditOpen && !isLoading && !loading && isEdit) {
      name && setValue("name", name);
      syncWithOutlook && setValue("syncWithOutlook", syncWithOutlook || false);
    }

  }, [loading, name, setValue, syncWithOutlook, isEditOpen, isLoading, isEdit])

  return (
    <CustomModal title="Law Type detail" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems='center'>
            <Typography variant="body2" fontSize='18px' fontWeight='medium'>
              {GENERAL_INFORMATION}
            </Typography>

            {isAdmin &&
              <Box>
                <IconButton color="primary" onClick={() => setIsEditOpen(!isEditOpen)} aria-label={isEditOpen ? 'back' : 'edit'}>
                  {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
                </IconButton>

                {isEditOpen &&
                  <IconButton color="primary" type='submit' disabled={isLoading} aria-label='save'>
                    <SaveIcon />
                  </IconButton>
                }
              </Box>
            }
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Typography variant="body2" fontSize='14px'>Title:</Typography>

              <Typography variant="h6" fontSize='16px' fontWeight='normal' textTransform='capitalize'>
                {titleize(name || '')}
              </Typography>

              <Typography variant="body2" fontSize='14px' mt={2}>Sync With Outlook:</Typography>

              <Typography variant="h6" fontSize='16px' fontWeight='normal' textTransform='capitalize'>
                {titleize(syncWithOutlook ? 'Sync enabled' : 'Sync disabled' || '')}
              </Typography>
            </Collapse>

            <Collapse in={isEditOpen}>
              <CustomController
                controllerName="name"
                controllerLabel="Title"
                fieldType="text"
              />
              <Box pt={2}>
                <Typography variant="h5" fontSize='14px'>Sync With Outlook:</Typography>

                <Controller
                  name="syncWithOutlook"
                  control={control}
                  defaultValue={syncWithOutlook}
                  render={({ field: { value } }) => (
                    <Switch
                      onChange={handleChangeSwitch}
                      checked={value as boolean}
                    />
                  )}
                />
              </Box>
            </Collapse>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}